<template>
	<ul v-if="dossierStore.selectedDossier" class="settings-list">
		<SettingsListRow name="Naam veranderen">
			<div class="input-wrapper">
				<input
					v-model="newDossierName"
					class="border rounded bg-white w-96"
					@blur="updateDossierName"
					@keydown.enter="($event.target as HTMLInputElement).blur()"
					@keydown.escape="cancelDossierNameEdit"
				/>
			</div>
		</SettingsListRow>
		<SettingsListRow name="Rekendatum">
			<div class="rekendatum-input">
				<OptionInput
					v-if="!showFullDateInput"
					:model-value="selectedYear"
					:options="yearOptions"
					@update:model-value="selectYear"
				/>
				<div v-else class="input-wrapper">
					<input
						v-model="newRekendatum"
						class="border rounded bg-white w-32 leading-[22px]"
						placeholder="dd-mm-jjjj"
						@blur="updateRekendatum"
						@keydown.enter="($event.target as HTMLInputElement).blur()"
					/>
				</div>
				<AppButton
					v-if="!showFullDateInput"
					variant="link"
					class="select-none!"
					@click="switchToFullDate"
				>
					datum kiezen
				</AppButton>
				<AppButton
					v-else
					variant="link"
					class="select-none!"
					@click="revertToYearOnly"
				>
					jaar kiezen
				</AppButton>
			</div>
		</SettingsListRow>
		<SettingsListRow name="Dossier overdragen">
			<form
				v-if="transferDossier.usersEligibleForTransferOptions.value?.length"
				class="inline-input-button"
				@submit.prevent="transferDossier.updateDossierOwner"
			>
				<DropdownInput
					v-model="transferDossier.newDossierOwnerId.value"
					:options="[
						{ value: undefined, label: 'Selecteren...' },
						...transferDossier.usersEligibleForTransferOptions.value,
					]"
				/>
				<AppButton
					type="submit"
					:variant="
						transferDossier.transferSucceeded.value ? 'success' : 'secondary'
					"
					:icon="
						transferDossier.transferSucceeded.value ? 'check' : 'handshake'
					"
					:disabled="
						transferDossier.transferSucceeded.value ||
						!transferDossier.newDossierOwnerId.value
					"
				>
					{{
						transferDossier.transferSucceeded.value
							? "Overgedragen"
							: "Overdragen"
					}}
				</AppButton>
			</form>
			<AppLabel v-else :label="label" />
		</SettingsListRow>
		<SettingsListRow name="Dossier importeren">
			<div class="file-picker-wrapper">
				<AppButton variant="secondary" icon="upload_file">
					Importeren
				</AppButton>
				<label for="file-picker" class="file-picker-label" />
				<input
					id="file-picker"
					type="file"
					accept="application/json"
					@change="importFile"
				/>
			</div>
		</SettingsListRow>
		<SettingsListRow name="Dossier verwijderen" class="danger">
			<AppButton
				variant="danger"
				:icon="loadingStore.deletingDossier ? 'auto_delete' : 'delete_outline'"
				:disabled="loadingStore.deletingDossier"
				@click.stop="deleteDossier(dossierStore.selectedDossier)"
			>
				{{ loadingStore.deletingDossier ? "Verwijderen..." : "Verwijderen" }}
			</AppButton>
		</SettingsListRow>
	</ul>
</template>

<script setup lang="ts">
import { useTransferDossier } from "@/composables/useTransferDossier";
import { useZero } from "@/composables/useZero";
import { confirmDelete } from "@/helpers/interactionHelpers";
import router from "@/router";
import { useLoadingStore } from "@/store/useLoadingStore";
import { ref, watch, computed } from "vue";
import { LabelType } from "@/enums/LabelType";
import { DateTime } from "luxon";
import { fuzzyDateParse } from "@loanlink-nl/migration";
import { useDossierStore } from "@/store/useDossierStore";
import { useDossierDataStore } from "@/store/useDossierDataStore";
import type { Row } from "@rocicorp/zero";
import { mutators } from "@loanlink-nl/zero";
import { useCalculationDate } from "@/composables/useCalculationDate";

const dossierStore = useDossierStore();
const dossierDataStore = useDossierDataStore();
const loadingStore = useLoadingStore();
const z = useZero();

const transferDossier = useTransferDossier(
	() => dossierStore.selectedDossier?.id,
);

const newDossierName = ref(dossierStore.selectedDossier?.name);

watch(
	() => dossierStore.selectedDossier?.name,
	(name) => {
		newDossierName.value = name;
	},
);

function isYearOnly(value?: string): boolean {
	return !!value && /^\d{4}$/.test(value);
}

function formatDateForInput(isoDate?: string): string {
	// If year-only, return empty (the OptionInput handles year display)
	if (isYearOnly(isoDate)) {
		return "";
	}
	const date = fuzzyDateParse(isoDate);
	return date.isValid ? date.toFormat("d-M-yyyy") : "";
}

const { calculationDate, calculationDateString, calculationDateIsYearOnly } =
	useCalculationDate();

// Show full date input if the current value is not year-only
const showFullDateInput = ref(!calculationDateIsYearOnly.value);

const yearOptions = computed(() => {
	const now = DateTime.now();
	const currentYear = now.year;
	const currentMonth = now.month;

	const years: number[] = [];

	// Show previous year until end of January (month 1)
	if (currentMonth <= 1) {
		years.push(currentYear - 1);
	}

	// Always show current year
	years.push(currentYear);

	// Show next year from November onwards (month >= 11)
	if (currentMonth >= 11) {
		years.push(currentYear + 1);
	}

	return years.map((year) => ({
		value: year,
		label: String(year),
	}));
});

const selectedYear = computed(() => {
	if (calculationDateIsYearOnly.value) {
		const year = parseInt(
			dossierDataStore.data.financieringsOpzet?.rekendatum ?? "",
			10,
		);
		if (yearOptions.value.some((opt) => opt.value === year)) {
			return year;
		}
	}
	return undefined;
});

// If year-only but year is not in available options, convert to full date
watch(
	[
		calculationDateIsYearOnly,
		yearOptions,
		() => dossierDataStore.data.financieringsOpzet?.rekendatum,
	],
	([isYearOnly, options]) => {
		if (!isYearOnly) return;

		const year = parseInt(
			dossierDataStore.data.financieringsOpzet?.rekendatum ?? "",
			10,
		);
		const yearIsAvailable = options.some((opt) => opt.value === year);

		if (!yearIsAvailable) {
			// Convert to full date using the calculated date from useCalculationDate
			dossierDataStore.data.financieringsOpzet.rekendatum =
				calculationDate.value.toISODate() ?? undefined;
			showFullDateInput.value = true;
		}
	},
	{ immediate: true },
);

function selectYear(year: number | string | boolean | undefined) {
	if (typeof year === "number") {
		dossierDataStore.data.financieringsOpzet.rekendatum = String(year);
	}
}

function switchToFullDate() {
	// Convert year-only to full date using the calculated date from useCalculationDate
	dossierDataStore.data.financieringsOpzet.rekendatum =
		calculationDate.value.toISODate() ?? undefined;
	showFullDateInput.value = true;
}

function revertToYearOnly() {
	// Use the year from the current date, fallback to current year if not available
	const dateYear = calculationDate.value.year;
	const yearIsAvailable = yearOptions.value.some(
		(opt) => opt.value === dateYear,
	);
	const year = yearIsAvailable ? dateYear : DateTime.now().year;
	dossierDataStore.data.financieringsOpzet.rekendatum = String(year);
	showFullDateInput.value = false;
}

const newRekendatum = ref(formatDateForInput(calculationDateString.value));
watch(
	() => calculationDateString.value,
	(rekendatum) => {
		newRekendatum.value = formatDateForInput(rekendatum);
	},
);

function setNewRekendatum() {
	newRekendatum.value = formatDateForInput(calculationDateString.value);
}

function updateRekendatum() {
	if (newRekendatum.value.trim() === "") {
		dossierDataStore.data.financieringsOpzet.rekendatum = undefined;
		setNewRekendatum();
		return;
	}

	const parsed = fuzzyDateParse(newRekendatum.value);
	if (parsed.isValid) {
		dossierDataStore.data.financieringsOpzet.rekendatum = parsed.toISODate();
	}
	setNewRekendatum();
}

function updateDossierName() {
	if (!dossierStore.selectedDossier) {
		return;
	}

	if (
		newDossierName.value &&
		newDossierName.value !== dossierStore.selectedDossier.name
	) {
		z.value.mutate(
			mutators.dossier.setName({
				id: dossierStore.selectedDossier.id,
				name: newDossierName.value,
			}),
		);
	}
}

function cancelDossierNameEdit(event: KeyboardEvent) {
	newDossierName.value = dossierStore.selectedDossier?.name;
	(event.target as HTMLInputElement).blur();
}

async function importFile(event: Event) {
	const target: HTMLInputElement = event.target as HTMLInputElement;
	await useDossierDataStore().loadFileData(target.files);
}

async function deleteDossier(dossier: Row["dossier"]) {
	if (confirmDelete()) {
		const dossierDeleted = await dossierStore.deleteDossier(dossier);

		if (dossierDeleted) {
			await router.push({ name: "Dossiers" });
		}
	}
}

const label = {
	type: LabelType.Neutral,
	message:
		"Er zijn geen adviseurs waar dit dossier aan overgedragen kan worden",
};
</script>

<style lang="scss">
@use "@/style/mixins.scss" as mixins;

.settings-list {
	list-style-type: none;
	padding: 0;
	width: 100%;

	.file-picker-wrapper {
		position: relative;

		@supports selector(:has(:focus-visible)) {
			&:has(:focus-visible) {
				@include mixins.native-focus;
			}
		}

		@supports not selector(:has(:focus-visible)) {
			&:focus-within {
				@include mixins.native-focus;
			}
		}

		&:hover button {
			background-color: var(--background-secondary);
		}

		.file-picker-label,
		input {
			opacity: 0;
			width: 100%;
			height: 100%;
			position: absolute;
			top: 0;
			left: 0;
			bottom: 0;
			right: 0;
		}

		.file-picker-label {
			z-index: 2;
			cursor: pointer;
		}

		input {
			z-index: -1;
		}
	}

	.inline-input-button {
		display: flex;
		justify-content: stretch;
		gap: 8px;

		.dropdown-input {
			flex-grow: 1;
		}

		@media screen and (max-width: var(--mobile-size)) {
			flex-direction: column;

			.available-button {
				align-self: flex-end;
			}
		}
	}

	.rekendatum-input {
		display: flex;
		align-items: flex-end;
		flex-direction: column;
		gap: 4px;
	}
}
</style>
